import React, { useContext, useEffect, useState} from 'react'
import { LoadingContext, M_Button, M_CheckBox, M_InputTextField, BufferService } from '../M_Components'
import { useTranslation } from 'react-i18next'
import { Done, DownloadDoneOutlined } from '@mui/icons-material'

export default function ConfigSettings() {
  const ctx = useContext(LoadingContext)
  const { t } = useTranslation()
  const [settings, setSettings] = useState([])
  const [selectedSetting, setSelectedSetting] = useState(null)
  const [selectedFileNames, setSelectedFileNames] = useState({})

  const bufferService = new BufferService('configurationType')

  useEffect(() => {
    const fetchSettings = async () => {
      ctx.setIsLoad(true)
      try {
        const response = await bufferService.get()
        if (response?.name === 'AxiosError') {
          ctx.toastMessages('error', response.response.data.Errors.toString(), '')
          return
        }
        const items = response || []
        setSettings(items)
        if (items.length > 0) setSelectedSetting(items[0])
      } catch (error) {
        ctx.toastMessages('error', t('ErrorLoadingSettings'), '')
      } finally {
        ctx.setIsLoad(false)
      }
    }
    fetchSettings()
  }, [t])

  const saveSettings = async () => {
    if (!selectedSetting) return
    const array = selectedSetting.Settings
    const bufferServiceSettings = new BufferService('SettingsEntity/updateAllSettings')

    try {
      const res = await bufferServiceSettings.put(array)
      if (res?.name === 'AxiosError') {
        ctx.toastMessages('error', res.response.data.Errors.toString(), '')
        return
      }
      ctx.toastMessages('success', '', t("SettingsSaved"))
    } catch (error) {
      ctx.toastMessages('error', t("ErrorSavingSettings"), '')
    }
  }

  const updateFieldValue = (fieldId, value) => {
    if (!selectedSetting) return

    const updated = {
      ...selectedSetting,
      Settings: selectedSetting.Settings.map((f) =>
        f.Id === fieldId ? { ...f, Value: value } : f
      ),
    }

    setSelectedSetting(updated)
    setSettings((prev) =>
      prev.map((s) => (s.Id === updated.Id ? updated : s))
    )
  }

  const convertFileToBase64 = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader()
      reader.onload = () => {
        const result = typeof reader.result === 'string' ? reader.result : ''
        resolve(result)
      }
      reader.onerror = reject
      reader.readAsDataURL(file)
    })
  }

  const hasExistingFileValue = (value) => typeof value === 'string' && value.trim() !== ''
  const getBooleanValue = (value) => {
    if (typeof value === 'boolean') return value
    if (typeof value === 'string') {
      const normalized = value.trim().toLowerCase()
      return normalized === 'true' || normalized === '1'
    }
    if (typeof value === 'number') return value === 1
    return false
  }

  const getExtensionFromMimeType = (mimeType) => {
    const mimeToExtension = {
      'application/pdf': 'pdf',
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': 'xlsx',
      'application/vnd.ms-excel': 'xls',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document': 'docx',
      'application/msword': 'doc',
      'application/vnd.openxmlformats-officedocument.presentationml.presentation': 'pptx',
      'application/vnd.ms-powerpoint': 'ppt',
      'text/plain': 'txt',
      'text/csv': 'csv',
      'application/zip': 'zip',
      'image/jpeg': 'jpg',
      'image/png': 'png',
    }

    return mimeToExtension[mimeType] || 'bin'
  }

  const downloadFieldFile = (field) => {
    if (!hasExistingFileValue(field?.Value)) return

    const rawValue = field.Value.trim()
    const isDataUrl = rawValue.startsWith('data:')
    const mimeType = isDataUrl
      ? rawValue.substring(5, rawValue.indexOf(';')) || 'application/octet-stream'
      : 'application/octet-stream'

    const extension = getExtensionFromMimeType(mimeType)
    const fallbackFileName = `${field.Name || 'file'}.${extension}`
    const fileName = selectedFileNames[field.Id] || fallbackFileName
    const href = isDataUrl ? rawValue : `data:${mimeType};base64,${rawValue}`

    const link = document.createElement('a')
    link.href = href
    link.download = fileName
    link.click()
  }

  return (
    <div className="divGlobalSettings" data-testid="settings-container">
      <div className="subTitleSettings">
        <div>
          <div style={{ color: ctx.secondaryColor }} data-testid="change-password-title">
            {t('Settings')}
          </div>
          <div className="labelSubtitleSettings" data-testid="change-password-info">
            {t("InfoSettings")}
          </div>
        </div>
      </div>

      <div className="divSettingsGlobal" style={{ display: "flex", gap: "20px" }}>

        <div className="menuSettings" style={{ minWidth: "250px" }}>
          {settings.map((setting) => (
            <div
              key={setting.Id}
              className={`divMenuSettings ${selectedSetting?.Id === setting.Id ? "active" : ""}`}
              style={{ cursor: "pointer", padding: "10px", borderBottom: "1px solid #ccc" }}
              onClick={() => setSelectedSetting(setting)}
            >
              <h3 style={{ color: 'var(--color-red-dark)' }}>{setting.Name}</h3>
              <p style={{ color: 'var(--color-grey-dark)' }} className='MenuSubtitleSettings'>{setting.Description}</p>
            </div>
          ))}
        </div>

        <div className="divFormSettings" style={{ flex: 1 }}>
          {selectedSetting ? (
            <div >
              <h2 style={{ color: 'var(--color-red-dark)' }}>{selectedSetting.Name}</h2>
              <div className='divItemFormSettings'>
                {selectedSetting.Settings?.map((field) => (
                  <div key={field.Id} >
                    {field.FieldType === 8 ? (
                      <div>
                        <label>{field.Name}</label>
                        <input
                          type="file"
                          onChange={async (e) => {
                            const file = e.target.files?.[0]
                            if (!file) {
                              updateFieldValue(field.Id, '')
                              setSelectedFileNames((prev) => ({ ...prev, [field.Id]: '' }))
                              return
                            }

                            try {
                              const base64Value = await convertFileToBase64(file)
                              updateFieldValue(field.Id, base64Value)
                              setSelectedFileNames((prev) => ({ ...prev, [field.Id]: file.name }))
                            } catch (error) {
                              ctx.toastMessages('error', t("ErrorSavingSettings"), '')
                            }
                          }}
                        />
                        <br /><br />
                        {selectedFileNames[field.Id] ? (
                          <p>{selectedFileNames[field.Id]}</p>
                        ) : hasExistingFileValue(field.Value) ? (
                          <p>Ficheiro carregado</p>
                        ) : null}
                        {hasExistingFileValue(field.Value) ? (
                          <div>
                            <M_Button
                              label="Download"
                              onClick={() => downloadFieldFile(field)}
                              icon={<DownloadDoneOutlined />}
                              borderColor={'var(--color-grey)'}
                            />
                          </div>
                        ) : null}
                      </div>
                    ) : field.FieldType === 2 ? (
                      <div className='divCheckboxFormQuotes'>
                        <M_CheckBox
                          label={t(field.Name)}
                          checked={getBooleanValue(field.Value)}
                          onChange={(e) => updateFieldValue(field.Id, e.target.checked)}
                          color={"var(--color-red-dark)"}
                        />
                      </div>
                    ) : (
                      <M_InputTextField
                        type={field.FieldType === 1 ? 'decimal' : 'text'}
                        label={field.Name}
                        value={field.Value || ""}
                        onChange={(e) => updateFieldValue(field.Id, e.target.value)}
                      />
                    )}
                  </div>
                ))}
              </div>
              <div className='divButtonFormSettings'>
                <M_Button
                  label={t("Save")}
                  backgroundColor={'green'}
                  labelColor={'white'}
                  icon={<Done style={{ color: 'var(--color-white)' }} />}
                  onClick={saveSettings}
                />
              </div>
            </div>
          ) : (
            <p>Selecione um item no menu para editar as configurações</p>
          )}
        </div>
      </div>
    </div>
  )
}

